import { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle, TrendingUp, Loader2 } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { chartColors } from "@/lib/theme/chartTheme";

interface Exposure {
  category: string;
  name: string;
  exposure: number;
  percentage: number;
  alert: boolean;
  confidence: "High" | "Medium" | "Low";
}

// Heuristic sector classification
const classifySector = (name: string, symbol: string): string => {
  const n = (name || '').toLowerCase();
  const s = (symbol || '').toUpperCase();

  // Technology
  if (n.includes('tech') || n.includes('software') || n.includes('microsoft') || n.includes('apple') ||
      n.includes('nvidia') || n.includes('google') || n.includes('amazon') || n.includes('meta') ||
      n.includes('salesforce') || n.includes('adobe') || n.includes('oracle') ||
      ['AAPL', 'MSFT', 'NVDA', 'GOOGL', 'AMZN', 'META', 'CRM', 'ADBE', 'ORCL'].includes(s)) {
    return 'Technology';
  }

  // Healthcare
  if (n.includes('health') || n.includes('pharma') || n.includes('bio') || n.includes('medical') ||
      n.includes('johnson') || n.includes('pfizer') || n.includes('unitedhealth') ||
      ['JNJ', 'PFE', 'UNH', 'MRK', 'ABBV', 'LLY'].includes(s)) {
    return 'Healthcare';
  }

  // Financials
  if (n.includes('bank') || n.includes('financial') || n.includes('jpmorgan') || n.includes('goldman') ||
      n.includes('morgan stanley') || n.includes('visa') || n.includes('mastercard') ||
      ['JPM', 'GS', 'MS', 'V', 'MA', 'BAC', 'C'].includes(s)) {
    return 'Financials';
  }

  // Real Estate
  if (n.includes('real estate') || n.includes('reit') || n.includes('property') || n.includes('realty')) {
    return 'Real Estate';
  }

  // Private Equity / VC
  if (n.includes('private equity') || n.includes('buyout') || n.includes('venture') || n.includes('growth equity')) {
    return 'Private Equity';
  }

  // Consumer
  if (n.includes('consumer') || n.includes('retail') || n.includes('walmart') || n.includes('costco')) {
    return 'Consumer';
  }

  // Energy
  if (n.includes('energy') || n.includes('oil') || n.includes('gas') || n.includes('exxon') || n.includes('chevron')) {
    return 'Energy';
  }

  return 'Other';
};

// Heuristic geography classification
const classifyGeography = (name: string, ccy: string): string => {
  const n = (name || '').toLowerCase();

  if (n.includes('china') || n.includes('hong kong') || n.includes('singapore') || n.includes('japan') ||
      n.includes('asia') || n.includes('apac') || n.includes('pacific')) {
    return 'Asia Pacific';
  }

  if (n.includes('europe') || n.includes('euro') || n.includes('uk') || n.includes('british') ||
      n.includes('german') || n.includes('french') || n.includes('swiss')) {
    return 'Europe';
  }

  if (n.includes('emerging') || n.includes('latin') || n.includes('brazil') || n.includes('india')) {
    return 'Emerging Markets';
  }

  // Default based on currency
  if (ccy === 'USD' || ccy === 'CAD') return 'North America';
  if (ccy === 'EUR' || ccy === 'GBP' || ccy === 'CHF') return 'Europe';
  if (ccy === 'JPY' || ccy === 'SGD' || ccy === 'HKD' || ccy === 'AUD') return 'Asia Pacific';

  return 'North America'; // Default
};

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const CHART_COLORS = [
  "hsl(var(--chart-1))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
];

export default function Exposures() {
  const { orgId, familyId } = useTenant();

  // Fetch holdings
  const { data: holdingsData, isLoading } = useQuery({
    queryKey: ['exposures-holdings', orgId, familyId],
    queryFn: async () => {
      if (!orgId) return [];

      let query = supabase
        .from('holdings')
        .select('*')
        .eq('org_id', orgId);

      if (familyId) {
        query = query.eq('family_id', familyId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
    enabled: !!orgId
  });

  // Compute exposures from holdings
  const { totalExposure, sectorExposures, geographyExposures, fxExposures, singleNameExposures, issuerExposures, alertCount } = useMemo(() => {
    if (!holdingsData || holdingsData.length === 0) {
      return {
        totalExposure: 0,
        sectorExposures: [],
        geographyExposures: [],
        fxExposures: [],
        singleNameExposures: [],
        issuerExposures: [],
        alertCount: 0
      };
    }

    const total = holdingsData.reduce((sum, h) => sum + (Number(h.mv) || 0), 0);

    const computeExposures = (
      category: string,
      classifier: (h: { name?: string; symbol?: string; mv?: number; ccy?: string }) => string,
      concentrationThreshold: number = 0.2
    ): Exposure[] => {
      const grouped: Record<string, number> = {};
      holdingsData.forEach(h => {
        const key = classifier(h);
        grouped[key] = (grouped[key] || 0) + (Number(h.mv) || 0);
      });

      return Object.entries(grouped)
        .map(([name, exposure]) => ({
          category,
          name,
          exposure,
          percentage: total > 0 ? (exposure / total) * 100 : 0,
          alert: total > 0 && (exposure / total) > concentrationThreshold,
          confidence: exposure > 1000000 ? 'High' as const : exposure > 100000 ? 'Medium' as const : 'Low' as const
        }))
        .sort((a, b) => b.exposure - a.exposure)
        .slice(0, 6);
    };

    const sectors = computeExposures('Sector', h => classifySector(h.name || '', h.symbol || ''));
    const geographies = computeExposures('Geography', h => classifyGeography(h.name || '', h.ccy || 'USD'));
    const currencies = computeExposures('FX', h => h.ccy || 'USD');
    const singleNames = computeExposures(
      'Single Name',
      h => h.name || h.symbol || 'Unknown',
      0.1
    );

    // Issuer exposures - for funds/alternatives, group by fund name pattern
    const issuers = computeExposures(
      'Issuer',
      h => {
        const name = h.name || h.symbol || 'Unknown';
        // For funds, use the fund name; for stocks, use company name
        if (name.toLowerCase().includes('fund') || name.toLowerCase().includes('lp') || name.toLowerCase().includes('partners')) {
          return name;
        }
        return name.split(' ')[0] + (name.split(' ')[1] ? ' ' + name.split(' ')[1] : '');
      },
      0.2
    );

    const alerts = [...sectors, ...geographies, ...currencies, ...singleNames, ...issuers].filter(e => e.alert).length;

    return {
      totalExposure: total,
      sectorExposures: sectors,
      geographyExposures: geographies,
      fxExposures: currencies,
      singleNameExposures: singleNames,
      issuerExposures: issuers,
      alertCount: alerts
    };
  }, [holdingsData]);

  const confidenceColor = (confidence: string) => {
    switch (confidence) {
      case "High": return "bg-success text-success-foreground";
      case "Medium": return "bg-warning text-warning-foreground";
      case "Low": return "bg-muted text-muted-foreground";
      default: return "bg-secondary text-secondary-foreground";
    }
  };

  const CustomTooltip = ({ active, payload }: { active?: boolean; payload?: Array<{ payload: { name: string; exposure: number; percentage: number } }> }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-background border rounded-lg p-3 shadow-lg">
          <p className="font-semibold">{data.name}</p>
          <p className="text-sm text-muted-foreground">
            {formatCurrency(data.exposure)}
          </p>
          <p className="text-sm font-mono">{data.percentage.toFixed(1)}%</p>
        </div>
      );
    }
    return null;
  };
  
  const ExposureChart = ({ title, data }: { title: string; data: Exposure[] }) => {
    const chartData = data.map(item => ({
      name: item.name,
      value: item.exposure,
      exposure: item.exposure,
      percentage: item.percentage,
      alert: item.alert,
      confidence: item.confidence,
    }));

    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-center justify-center">
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={90}
                    fill={chartColors[0]}
                    dataKey="value"
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="space-y-2 flex flex-col justify-center">
              {data.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 border rounded-lg">
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full flex-shrink-0" 
                      style={{ backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] }}
                    />
                    <span className="font-medium text-sm">{item.name}</span>
                    {item.alert && (
                      <AlertTriangle className="h-4 w-4 text-warning flex-shrink-0" />
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Badge variant="outline" className={confidenceColor(item.confidence)}>
                      {item.confidence}
                    </Badge>
                    <span className="font-mono text-xs">{item.percentage.toFixed(1)}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };
  
  if (isLoading) {
    return (
      <div className="flex-1 space-y-4 px-6 py-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">Loading exposure data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 space-y-4 px-6 py-6">

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Exposures</h1>
          <p className="text-muted-foreground">
            Sector, issuer, geography, and FX exposure analysis
          </p>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Exposure</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totalExposure)}</div>
            <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
              <TrendingUp className="h-3 w-3" />
              Across all asset classes
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Concentration Alerts</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-warning">{alertCount}</div>
            <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
              <AlertTriangle className="h-3 w-3" />
              Positions exceeding threshold
            </p>
          </CardContent>
        </Card>
      </div>

      {totalExposure === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <p>No holdings found. Upload statements to populate exposure data.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          <ExposureChart title="Sector Exposure" data={sectorExposures} />
          <ExposureChart title="Issuer Exposure" data={issuerExposures} />
          <ExposureChart title="Geographic Exposure" data={geographyExposures} />
          <ExposureChart title="Currency Exposure" data={fxExposures} />
          <ExposureChart title="Single Name Exposure" data={singleNameExposures} />
        </div>
      )}
    </div>
  );
}
